import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'

import { SvgIcon, SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { T, useI18n } from '../../../i18n/index.js'
import { ResultsView } from '../../SmartSearch.types.js'

type Props = {
  onItemClick?: (suggestion: string) => void
  view?: ResultsView
}

export const NoQuestionEmptyState = ({
  onItemClick,
  view = 'command-bar',
}: Props) => {
  const { $t } = useI18n()

  const items: { icon: SvgIconName; title: string }[] = [
    {
      icon: 'megaphone',
      title: $t({
        defaultMessage:
          'What are the trending topics in this community right now?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.TrendingTopics',
      }),
    },
    {
      icon: 'user-plus',
      title: $t({
        defaultMessage: 'How can I get involved and participate more?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.GetInvolved',
      }),
    },
    {
      icon: 'check-circle-broken',
      title: $t({
        defaultMessage:
          'What are the key guidelines I should know before posting?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.Guidelines',
      }),
    },
    {
      icon: 'target',
      title: $t({
        defaultMessage:
          'Which members or discussions should I follow for valuable insights?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.WhoToFollow',
      }),
    },
    {
      icon: 'help',
      title: $t({
        defaultMessage:
          "What's the best way to ask questions and get helpful answers?",
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.AskQuestions',
      }),
    },
    {
      icon: 'share',
      title: $t({
        defaultMessage:
          'How can I share my ideas and get constructive feedback?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.ShareIdeas',
      }),
    },
    {
      icon: 'refresh',
      title: $t({
        defaultMessage:
          'Can I get a quick recap of recent important discussions?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.RecentDiscussions',
      }),
    },
    {
      icon: 'sliders',
      title: $t({
        defaultMessage:
          'How do I personalize my experience, like updating my profile or settings?',
        id: 'SmartSearch.AskAI.NoQuestionEmptyState.Items.PersonalizeExperience',
      }),
    },
  ]

  const ItemsContainer = view === 'search-page' ? List : 'div'

  return (
    <div className="flex flex-col gap-4 px-2 py-4">
      <div className="font-semibold ps-4">
        <T
          defaultMessage="I can help with ..."
          id="SmartSearch.AskAI.NoQuestionEmptyState.Title"
        />
      </div>

      <ItemsContainer className="flex flex-col gap-1">
        {items.map(item => (
          <Combobox.Option
            className={({ active }) =>
              clsx(
                'flex cursor-pointer select-none items-baseline md:items-center px-4 rounded-md py-2 gap-3',
                active && 'bg-surface-subdued',
              )
            }
            key={item.title}
            value={{
              preventClose: true,
              item: item.title,
              action: () => onItemClick?.(item.title),
            }}
          >
            <SvgIcon
              name={item.icon}
              className="text-content-disabled relative translate-y-1 md:translate-y-0"
            />
            <span>{item.title}</span>
          </Combobox.Option>
        ))}
      </ItemsContainer>
    </div>
  )
}
